import { NavLink, Navigate, Route, Routes } from "react-router";
import { useMe } from "../lib/useMe.ts";
import Content from "./Content.tsx";
import Playlists from "./Playlists.tsx";
import Schedules from "./Schedules.tsx";
import Displays from "./Displays.tsx";
import Sources from "./Sources.tsx";
import Override from "./Override.tsx";
import "./admin.css";

/**
 * This component is mounted at the splat route `/admin/*` (see App.tsx), and
 * every link and redirect below is written ABSOLUTELY against this base.
 *
 * Relative targets are the obvious thing to write and they are a trap here.
 * Under react-router v7, a relative path inside a splat route resolves against
 * the matched splat portion rather than the route's base — so the catch-all
 * `<Navigate to="content">` sent `/admin/nonsense` to
 * `/admin/nonsense/content`, which matched the catch-all again, and again, and
 * again. Measured on the real bundle: an unbounded redirect loop that hangs the
 * tab. v6 resolved the same code against `/admin` and terminated, which is why
 * this only appeared on upgrade.
 */
const BASE = "/admin";

// Sections. Schedules, Displays, and Override are admin-only (enforced server-side too).
const SECTIONS = [
  { to: `${BASE}/content`, label: "Content", adminOnly: false },
  { to: `${BASE}/playlists`, label: "Playlists", adminOnly: false },
  { to: `${BASE}/schedules`, label: "Schedules", adminOnly: true },
  { to: `${BASE}/displays`, label: "Displays", adminOnly: true },
  { to: `${BASE}/sources`, label: "Sources", adminOnly: true },
  { to: `${BASE}/override`, label: "Override", adminOnly: true },
] as const;

export default function AdminApp() {
  const state = useMe();

  if (state.status === "loading") {
    return <div className="admin-splash">Loading…</div>;
  }
  if (state.status === "unauthenticated") {
    // Entra session missing — kick to SWA login (configured in staticwebapp.config.json).
    window.location.href = "/login";
    return <div className="admin-splash">Redirecting to sign in…</div>;
  }
  if (state.status === "forbidden") {
    // Signed in, but not on a church account. Don't redirect to /login — that
    // would sign them straight back in as the same user. Offer sign-out instead.
    return (
      <div className="admin-splash">
        <p>{state.message}</p>
        <p>
          <a href="/logout">Sign out</a> and try again with your church account.
        </p>
      </div>
    );
  }
  if (state.status === "error") {
    return <div className="admin-splash">Couldn’t load your account: {state.message}</div>;
  }

  const { me } = state;
  const isAdmin = me.role === "admin";

  return (
    <div className="admin-shell">
      <header className="admin-nav">
        <div className="admin-brand">
          <span className="admin-brand-mark" aria-hidden>✦</span>
          <span>Liberty Bible Church · Signage</span>
        </div>
        <nav className="admin-tabs">
          {SECTIONS.filter((s) => !s.adminOnly || isAdmin).map((s) => (
            <NavLink
              key={s.to}
              to={s.to}
              className={({ isActive }) =>
                "admin-tab" + (isActive ? " admin-tab--active" : "")
              }
            >
              {s.label}
            </NavLink>
          ))}
        </nav>
        <div className="admin-account">
          <span className="admin-role" data-role={me.role}>
            {me.role}
          </span>
          <span className="muted">{me.email}</span>
          <a className="btn btn--ghost" href="/logout">
            Sign out
          </a>
        </div>
      </header>

      <main className="admin-main">
        <Routes>
          <Route index element={<Navigate to={`${BASE}/content`} replace />} />
          <Route path="content" element={<Content />} />
          <Route path="playlists" element={<Playlists />} />
          <Route
            path="schedules"
            element={isAdmin ? <Schedules /> : <Forbidden />}
          />
          <Route
            path="displays"
            element={isAdmin ? <Displays /> : <Forbidden />}
          />
          <Route
            path="sources"
            element={isAdmin ? <Sources /> : <Forbidden />}
          />
          <Route
            path="override"
            element={isAdmin ? <Override /> : <Forbidden />}
          />
          <Route path="*" element={<Navigate to={`${BASE}/content`} replace />} />
        </Routes>
      </main>
    </div>
  );
}

function Forbidden() {
  return (
    <section className="admin-page">
      <h1>Admins only</h1>
      <p className="muted">
        Scheduling and display management require the admin role.
      </p>
    </section>
  );
}
